/* eslint-disable react/prop-types */
/**
 * V2NoteHost — native V2 host for the shared NoteDetailDialog + companion
 * dialogs (V2 Notes migration Phase 3B-2 Step 3, Jun 2026).
 *
 * Provides the full 134-entry ctx contract natively (no legacy Dashboard).
 * Core flows implemented for real: note/client/template loading, field
 * editing with the shared useNoteAutosaveUI engine, sign flows via the
 * lifted SignatureConfirmDialog, corrections, non-billable, link goals,
 * next-steps. Peripheral legacy-only affordances (reference panel,
 * appointment editing, note history panel) fall back to safe no-ops or a
 * deep link to the legacy editor. The legacy /note/:id route is untouched.
 */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { useStaticData } from '../../context/StaticDataContext';
import { useAppointments } from '../../context/AppointmentsContext';
import { useV2AppointmentDialogLegacy } from '../../hooks/useV2AppointmentDialogLegacy';
import { useV2BootstrapData } from '../../hooks/useV2BootstrapData';
import { AppointmentScheduleDialog } from './AppointmentScheduleDialog';
import { useNoteAutosaveUI } from '../NoteAutosaveWrapper';
import LazySignaturePad from '../LazySignaturePad';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';
import { Button } from '../ui/button';
import { NoteDetailDialog } from './NoteDetailDialog';
import { NoteHistoryPanel } from '../notes/NoteHistoryPanel';
import { SignatureConfirmDialog, CorrectionsDialog, NotBillableDialog } from './note-dialogs/NoteCompanionDialogs';
import { LinkGoalsDialog } from './note-dialogs/LinkGoalsDialog';
import { NextStepsDialog } from './note-dialogs/NextStepsDialog';
import { ScheduleNextDialog } from './note-dialogs/ScheduleNextDialog';
import { SignatureAdvanceDialog } from './note-dialogs/SignatureAdvanceDialog';
import { SendToClientDialog } from './SendToClientDialog';
import { AddEditMedicationDialog } from './AddEditMedicationDialog';
import { AddDiagnosisDialog } from './AddDiagnosisDialog';
import { isSuperAdmin } from '../../utils/helpers';
import { resolveNoteTemplate, mergeNoteContentWithTemplate, initializeNoteContentFromTemplate, classifyUnparsedContent } from '../../utils/noteTemplateMerge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NOTE_SIZE_PRESETS = {
  split: { width: '50vw', label: 'Split', icon: 'columns' },
  medium: { width: '75vw', label: '75%', icon: 'square' },
  full: { width: '95vw', label: 'Full', icon: 'maximize' },
};

const calculateAge = (dob) => {
  if (!dob) return null;
  const b = new Date(dob);
  if (isNaN(b)) return null;
  const t = new Date();
  let a = t.getFullYear() - b.getFullYear();
  const m = t.getMonth() - b.getMonth();
  if (m < 0 || (m === 0 && t.getDate() < b.getDate())) a--;
  return a;
};
const formatDateMMDDYYYY = (s) => {
  if (!s) return '';
  const d = String(s).slice(0, 10).split('-');
  return d.length === 3 ? `${d[1]}/${d[2]}/${d[0]}` : String(s);
};
const formatLocalDateTime = (date) => {
  if (!date) return '';
  const d = new Date(date);
  return isNaN(d) ? '' : d.toLocaleString();
};
const formatTimeTo12Hour = (t) => {
  if (!t) return '';
  const [h, m] = String(t).split(':');
  if (h === undefined || m === undefined) return t;
  const hr = parseInt(h, 10);
  const ampm = hr >= 12 ? 'PM' : 'AM';
  return `${hr % 12 || 12}:${m} ${ampm}`;
};

export default function V2NoteHost({ noteId: noteIdProp = null, onClose = null }) {
  const { noteId: noteIdParam } = useParams();
  const noteId = noteIdProp || noteIdParam;
  const embedded = !!onClose;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const returnTo = searchParams.get('return') || '/v2';
  // ?mode=view forces read-only even when the note status is editable
  const forceViewMode = searchParams.get('mode') === 'view';
  const { user, setUser } = useAuth();
  const staticData = useStaticData();
  const {
    providers = [], locations = [], posCodes = [], users = [],
    organizations = [], cptCodes = [], formTemplates = [], serviceTypes = [],
  } = staticData || {};

  // ── Core data ──────────────────────────────────────────────────────
  const [selectedNote, setSelectedNote] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientNotes, setClientNotes] = useState([]);
  const [clientAppointments, setClientAppointments] = useState([]);
  const [clientDiagnoses, setClientDiagnoses] = useState([]);
  const [clientMedications, setClientMedications] = useState([]);
  const [clientVitals, setClientVitals] = useState([]);
  // Jun 2026 — native Add Medication / Add Diagnosis from note fields
  // (legacy parity; these were noop-stubbed so the buttons did nothing).
  const [showMedicationForm, setShowMedicationForm] = useState(false);
  const [editingMedication, setEditingMedication] = useState(null);
  // Jun 2026 — Medication Plan capture: when set, the Add Medication dialog
  // routes its result into the note's medication_plan field instead of the chart.
  const [medPlanCapture, setMedPlanCapture] = useState(null);
  const [medSnapshotCapture, setMedSnapshotCapture] = useState(null);
  const [showDiagnosisForm, setShowDiagnosisForm] = useState(false);
  const [clientGroups] = useState([]);
  const [loadError, setLoadError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fallbackUsers, setFallbackUsers] = useState([]);
  const effectiveUsers = users && users.length ? users : fallbackUsers;
  const [fallbackTemplates, setFallbackTemplates] = useState([]);
  const effectiveTemplates = formTemplates && formTemplates.length ? formTemplates : fallbackTemplates;

  // StaticDataContext is populated by the legacy dashboard on login;
  // when landing directly on /v2/note it can be empty — fetch fallbacks
  // so provider names resolve and template fields render.
  useEffect(() => {
    if (users && users.length) return;
    let cancelled = false;
    axios.get(`${API}/users`).then(r => {
      if (!cancelled) setFallbackUsers(Array.isArray(r.data) ? r.data : (r.data?.users || []));
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [users]);
  const [templatesBundleMiss, setTemplatesBundleMiss] = useState(false);
  useEffect(() => {
    if (formTemplates && formTemplates.length) return;
    if (!templatesBundleMiss) return; // open-bundle supplies templates; only fetch when it didn't
    let cancelled = false;
    axios.get(`${API}/form-templates`).then(r => {
      if (!cancelled) setFallbackTemplates(Array.isArray(r.data) ? r.data : (r.data?.templates || []));
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [formTemplates, templatesBundleMiss]);

  // ── Editing state ──────────────────────────────────────────────────
  const [showNoteDetail, setShowNoteDetail] = useState(true);
  const [isEditingNote, setIsEditingNote] = useState(false);
  const [editedNoteContent, setEditedNoteContent] = useState({});
  const [editedNoteConsent, setEditedNoteConsent] = useState(null);
  const [linkedGoals, setLinkedGoals] = useState([]);
  const [isSavingNote, setIsSavingNote] = useState(false);
  const [autosaveBlockedReason, setAutosaveBlockedReason] = useState(null);
  const [noteCreationError, setNoteCreationError] = useState(null);
  const [showCloseEditingNoteWarning, setShowCloseEditingNoteWarning] = useState(false);
  const [loadedSignatures, setLoadedSignatures] = useState({});
  const [linkedFormsAdded, setLinkedFormsAdded] = useState({});
  const [noteLinkedAppointment, setNoteLinkedAppointment] = useState(null);
  const [noteJustSigned, setNoteJustSigned] = useState(false);
  const [noteDialogSize, setNoteDialogSize] = useState('full');
  // Jun 2026 V2 action parity — these were noop-stubbed (dead buttons):
  // right-click edit context menu + post-sign "Schedule Next" request.
  const [showEditContextMenu, setShowEditContextMenu] = useState(false);
  const [editContextMenuPos, setEditContextMenuPos] = useState({ x: 0, y: 0 });
  const [showScheduleNextDialog, setShowScheduleNextDialog] = useState(false);
  const [scheduleNextData, setScheduleNextData] = useState({ timeframe: '2_weeks', customDate: '', notes: '' });

  // ── Companion dialog state ─────────────────────────────────────────
  const [showSignatureConfirm, setShowSignatureConfirm] = useState(false);
  const [signatureAction, setSignatureAction] = useState('provider');
  const [signatureDate, setSignatureDate] = useState(new Date().toISOString());
  const [signWithoutDate, setSignWithoutDate] = useState(false);
  const [showCorrectionsDialog, setShowCorrectionsDialog] = useState(false);
  const [correctionsReason, setCorrectionsReason] = useState('');
  const [showNotBillableDialog, setShowNotBillableDialog] = useState(false);
  const [notBillableTargetNote, setNotBillableTargetNote] = useState(null);
  const [notBillableReason, setNotBillableReason] = useState('');
  const [notBillableComment, setNotBillableComment] = useState('');
  const [showLinkGoalsDialog, setShowLinkGoalsDialog] = useState(false);
  const [availableTreatmentPlanGoals, setAvailableTreatmentPlanGoals] = useState([]);
  const [loadingTreatmentPlanGoals, setLoadingTreatmentPlanGoals] = useState(false);
  const [showNextStepsDialog, setShowNextStepsDialog] = useState(false);
  // Legacy parity (App.js ~L5469): CPT × insurance restriction check for
  // the post-sign Next Steps dialog (supervisor branch CTA + banner).
  const [nextStepsCptBlocked, setNextStepsCptBlocked] = useState(false);
  const [showNoteHistory, setShowNoteHistory] = useState(false);
  // Oct 2026 — signature right-click menu (Sign / Remove my signature).
  // Was stubbed as noops, so V2 lacked legacy's "Remove Signature" option.
  const [showSignatureContextMenu, setShowSignatureContextMenu] = useState(false);
  const [signatureContextMenuPos, setSignatureContextMenuPos] = useState({ x: 0, y: 0 });
  const [signatureContextMenuType, setSignatureContextMenuType] = useState(null);
  const [showSendToClient, setShowSendToClient] = useState(false);
  const sentToClientRef = useRef(false);
  const [showReferencePanel, setShowReferencePanel] = useState(false);
  const [showSignaturePad, setShowSignaturePad] = useState(false);
  const [pendingSignType, setPendingSignType] = useState('provider');
  const pendingSignArgsRef = useRef(null);
  // Legacy-parity post-provider-sign dialog ("Note Signed Successfully!").
  const [showSignatureAdvanceDialog, setShowSignatureAdvanceDialog] = useState(false);
  const [signedNoteForAdvancement, setSignedNoteForAdvancement] = useState(null);
  const [savingSignature, setSavingSignature] = useState(false);
  const sigPadRef = useRef(null);

  // Jun 2026 — iframe-safe promise-backed confirm (legacy App.js parity).
  // window.confirm is silently suppressed in the preview sandbox AND the
  // old callback-style confirmAction broke `await confirmAction(...)`
  // callers in NoteDetailDialog (co-sign "Remove Signature").
  const [confirmState, setConfirmState] = useState({
    open: false, title: '', message: '', confirmLabel: 'Confirm',
    cancelLabel: 'Cancel', destructive: false, resolver: null,
  });
  const confirmAction = useCallback((message, opts = {}) => new Promise((resolve) => {
    setConfirmState({
      open: true,
      title: opts.title || 'Please confirm',
      message: message || '',
      confirmLabel: opts.confirmLabel || 'Confirm',
      cancelLabel: opts.cancelLabel || 'Cancel',
      destructive: !!opts.destructive,
      resolver: resolve,
    });
  }), []);
  const closeConfirm = useCallback((answer) => {
    setConfirmState((prev) => {
      if (prev.resolver) prev.resolver(answer);
      return { ...prev, open: false, resolver: null };
    });
  }, []);

  const noteEditingAutosaveRef = useRef(null);
  const closeHost = useCallback(() => {
    // Blur first so a focused DebouncedTextarea commits its last keystrokes,
    // then flush + close on the next tick so the committed value reaches the
    // autosave refs (Escape never blurs; sub-100ms keystrokes were lost).
    try { document.activeElement?.blur?.(); } catch { /* noop */ }
    setTimeout(() => {
      // Final-save flush before leaving (fire-and-forget: the XHR survives
      // unmount and flushSave itself skips when nothing changed). Covers the
      // header X, Escape/overlay dismiss and post-sign paths — sub-debounce
      // edits were lost on quick close before this (Jun 2026 fix).
      noteEditingAutosaveRef.current?.flushSave?.();
      if (onClose) onClose(); else navigate(returnTo);
    }, 60);
  }, [onClose, navigate, returnTo]);

  // ── Load note + client ─────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    setTemplatesBundleMiss(false);
    (async () => {
      try {
        // Jun 2026 — single bundled request (legacy parity) replaces the
        // old sequential note → diagnoses → medications → templates loads.
        const { data: bundle } = await axios.get(`${API}/notes/${noteId}/open-bundle`);
        if (cancelled) return;
        const note = bundle.note;
        const bundleTemplates = Array.isArray(bundle.form_templates) && bundle.form_templates.length
          ? bundle.form_templates : formTemplates;
        if (Array.isArray(bundle.form_templates) && bundle.form_templates.length) setFallbackTemplates(bundle.form_templates);
        else setTemplatesBundleMiss(true);
        if (bundle.linked_appointment) setNoteLinkedAppointment(bundle.linked_appointment);
        setClientDiagnoses(bundle.client_diagnoses || []);
        setClientMedications(bundle.client_medications || []);
        setSelectedNote(note);
        let parsed = note.content;
        let parseFailed = false;
        if (typeof parsed === 'string') { try { parsed = JSON.parse(parsed); } catch { parsed = null; parseFailed = true; } }
        parsed = (parsed && typeof parsed === 'object') ? parsed : {};
        // Oct 2026 — classify unparseable content like legacy openNote:
        // corrupt JSON-looking content opens VIEW-ONLY (protects it from
        // being blank-initialized + autosaved over); plain text is legacy
        // content carried into the template init below.
        let contentLoadFailed = false;
        let legacyPlainText = null;
        if (parseFailed) {
          const cls = classifyUnparsedContent(note.content);
          contentLoadFailed = cls.corrupt;
          legacyPlainText = cls.legacyPlainText;
        }
        // Oct 2026 fix — merge the saved content against the note's form
        // template (same code path as legacy openNote) so matrix/template
        // structure, field order and template-only fields are present.
        // Resolving the template is awaited HERE, before `loading` clears,
        // so the form never renders before its template arrives.
        if (contentLoadFailed) {
          toast.error('This note could not be fully loaded, so it opened in view-only mode to protect its content. Try reopening it.');
          setEditedNoteContent(null);
        } else if (parsed.fields?.length > 0 || parsed.sections?.length > 0) {
          const template = await resolveNoteTemplate({
            noteData: parsed, fullNote: note, templates: bundleTemplates, api: API,
          });
          if (cancelled) return;
          parsed = mergeNoteContentWithTemplate(parsed, note, template);
          setEditedNoteContent(parsed);
        } else {
          // MISSING content — initialize the full field list from the
          // template (mirror of legacy openNote).  This both renders the
          // form and gives the autosave engine a non-empty structure, so
          // its empty-note AUTO-RESTORE / manual recovery banner can run
          // (the draft check defers forever on a bare `{}`).
          const template = await resolveNoteTemplate({
            noteData: parsed, fullNote: note, templates: bundleTemplates, api: API,
          });
          let initClient = null;
          const initDx = bundle.client_diagnoses || [];
          const initMeds = bundle.client_medications || [];
          if (note.client_id) {
            const cR = await axios.get(`${API}/clients/${note.client_id}`).catch(() => null);
            if (cR) initClient = cR.data;
          }
          if (cancelled) return;
          parsed = initializeNoteContentFromTemplate({
            fullNote: note, template, client: initClient,
            diagnoses: initDx, medications: initMeds, legacyPlainText,
          });
          setEditedNoteContent(parsed);
        }
        setLinkedGoals(note.linked_goals || []);
        // Mirror legacy openNote (App.js): enter edit mode whenever the
        // user CAN edit this note and it isn't locked — not just for
        // draft/needs_corrections (Jun 2026 fix: pencil was opening
        // signed/completed notes in view mode).
        const _role = (user?.role || '').toLowerCase();
        const _canEdit = !!user && (
          isSuperAdmin(user)
          || ['admin', 'office', 'office staff', 'biller'].includes(_role)
          || note.provider_id === user?.id || note.created_by === user?.id
          || note.assigned_supervisor === user?.id || note.assigned_supervisor_2 === user?.id
        );
        const _isDraft = note.status === 'draft';
        const editable = !forceViewMode && !contentLoadFailed
          && note.status !== 'locked' && note.is_locked !== true
          && (_canEdit || (_isDraft && (!note.provider_id || note.provider_id === user?.id)));
        setIsEditingNote(editable);
        // Mirror legacy openNote exactly (App.js): edit mode seeds the
        // consent header from the note's telehealth_consent, falling
        // back to the null-fields object.  A NON-NULL initial value is
        // required — the shared autosave hook only tracks consent
        // changes when the previous value exists, so a null init made
        // consent edits silently unsaveable (Jun 2026 fix).
        setEditedNoteConsent(
          editable
            ? (note.telehealth_consent
                ? { ...note.telehealth_consent }
                : { provider_location: null, pos_code: null, pos_description: null, modality: null })
            : null,
        );
        if (note.client_id) {
          const [cRes, nRes, aRes] = await Promise.allSettled([
            axios.get(`${API}/clients/${note.client_id}`),
            axios.get(`${API}/notes?client_id=${note.client_id}&limit=100`),
            axios.get(`${API}/appointments?client_id=${note.client_id}&summary=true`),
          ]);
          if (cancelled) return;
          if (cRes.status === 'fulfilled') setSelectedClient(cRes.value.data);
          if (nRes.status === 'fulfilled') {
            const list = Array.isArray(nRes.value.data) ? nRes.value.data : (nRes.value.data?.notes || []);
            setClientNotes(list);
          }
          if (aRes.status === 'fulfilled') {
            const appts = Array.isArray(aRes.value.data) ? aRes.value.data : (aRes.value.data?.appointments || []);
            setClientAppointments(appts);
          }
          axios.get(`${API}/clients/${note.client_id}/vitals`).then(r => !cancelled && setClientVitals(r.data?.vitals || r.data || [])).catch(() => {});
        }
      } catch (e) {
        if (!cancelled) { setTemplatesBundleMiss(true); setLoadError(e.response?.data?.detail || 'Failed to load note'); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [noteId, forceViewMode]);

  // ── Autosave (shared engine) ───────────────────────────────────────
  // Jun 2026 fix — do NOT gate on non-empty content: an EMPTY note is
  // exactly the case where the engine's draft check / empty-note
  // auto-restore must run (legacy gates on the template STRUCTURE which
  // is never empty, so restore worked there but not in V2).  The hook
  // internally skips saving empty formData, so this is save-safe; by
  // the time selectedNote is set, editedNoteContent has been seeded
  // from note.content in the same load effect.
  const autosaveEnabled = isEditingNote && !!selectedNote;
  const noteEditingAutosave = useNoteAutosaveUI({
    noteId: selectedNote?.id || null,
    setNoteId: () => {}, // v2-na: note already exists — autosave never creates here
    formData: editedNoteContent,
    consentData: editedNoteConsent,
    linkedGoals,
    metadata: selectedNote ? {
      date_of_service: selectedNote.date_of_service,
      start_time: selectedNote.start_time,
      end_time: selectedNote.end_time,
      units: selectedNote.units,
      cpt_code: selectedNote.cpt_code,
      pos_code: selectedNote.pos_code,
      modality: selectedNote.modality,
      ready_for_billing: selectedNote.ready_for_billing,
    } : null,
    enabled: autosaveEnabled,
    initialVersion: selectedNote?.current_version || null,
    serverUpdatedAt: selectedNote?.updated_at || null,
    createNoteData: null,
    onNoteCreated: () => {}, // v2-na: note already exists — autosave never creates here
    onSaveError: setAutosaveBlockedReason,
    onSaveCriticalError: setNoteCreationError,
    clientName: selectedNote?.client_name || null,
    providerName: effectiveUsers?.find(u => u.id === selectedNote?.provider_id)?.full_name || null,
    clientId: selectedNote?.client_id || null,
    noteEmptyManuallySaved: selectedNote?.empty_content_manually_saved || false,
    onFormDataRestore: (restored) => {
      if (restored.formData) setEditedNoteContent(restored.formData);
      if (restored.consentData !== undefined) setEditedNoteConsent(restored.consentData);
      if (restored.linkedGoals) setLinkedGoals(restored.linkedGoals);
      // Parity with legacy: draft restores toast here (version restores
      // already toast from the history panel).
      if (restored.source !== 'version_restore') {
        toast.success('Draft restored! Your unsaved changes have been recovered.', { duration: 4000 });
      }
    },
  });
  noteEditingAutosaveRef.current = noteEditingAutosave;

  // ── Shared helpers ─────────────────────────────────────────────────
  const refreshNotes = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/notes/${noteId}`);
      setSelectedNote(data);
    } catch { /* noop */ }
  }, [noteId]);
  const refreshTodos = useCallback(() => {}, []);
  const refreshClientNotes = useCallback(async () => {
    if (!selectedNote?.client_id) return;
    try {
      const { data } = await axios.get(`${API}/notes?client_id=${selectedNote.client_id}&limit=100`);
      setClientNotes(Array.isArray(data) ? data : (data?.notes || []));
    } catch { /* noop */ }
  }, [selectedNote?.client_id]);

  const updateNoteStatusOptimistically = useCallback((id, status, extra = {}) => {
    // status === undefined → signature-only update; keep existing status.
    const statusPatch = status !== undefined ? { status } : {};
    setSelectedNote(prev => (prev && prev.id === id ? { ...prev, ...statusPatch, ...extra } : prev));
    setClientNotes(prev => prev.map(n => (n.id === id ? { ...n, ...statusPatch, ...extra } : n)));
  }, []);

  const userRoleName = (user?.role || '').toLowerCase();
  const canEditNote = useCallback((note) => {
    if (!user || !note) return false;
    if (isSuperAdmin(user)) return true;
    if (note.status === 'locked' || note.is_locked === true) return false;
    return note.provider_id === user.id || note.created_by === user.id
      || note.assigned_supervisor === user.id
      || note.assigned_supervisor_2 === user.id
      || ['admin', 'office', 'office staff', 'biller'].includes(userRoleName);
  }, [user, userRoleName]);
  const canLockNote = useCallback((note) => {
    if (!user || !note) return false;
    if (isSuperAdmin(user)) return true;
    const perms = (user.permissions || user.role_permissions || user.effective_permissions || []);
    return ['office', 'office staff', 'biller'].includes(userRoleName)
      || (Array.isArray(perms) && perms.includes('lock_unlock_notes'));
  }, [user, userRoleName]);

  const openSignatureConfirm = useCallback((type) => {
    setSignatureAction(type === 'supervisor_2' ? 'supervisor' : type);
    setSignatureDate(new Date().toISOString());
    setSignWithoutDate(false);
    setShowSignatureConfirm(true);
  }, []);

  // Oct 2026 — legacy-parity direct signing (user report: V2's click-to-
  // sign forced a date/next-steps dialog; legacy signs immediately and the
  // Next Steps dialog opens on close via noteJustSigned).  The date dialog
  // remains reachable via right-click → "Sign with custom date/time…".
  const signNoteDirect = useCallback(async (type, withDate = true, customDate = null, forceSign = false) => {
    const signedAt = withDate ? (customDate || new Date().toISOString()) : null;
    try {
      const { data: updated } = await axios.put(`${API}/notes/${noteId}`, {
        [`${type}_signed_by`]: user.id,
        [`${type}_signed_at`]: signedAt,
        force_sign: forceSign,
      });
      // Backend "wrong-supervisor soft-route": the signer isn't the note's
      // assigned supervisor, so the signature was NOT stamped — the note
      // was routed to the assigned supervisor instead.  Surface that
      // honestly (user report: "it says signed as supervisor, but I don't
      // see the signature").
      const softRouted = type !== 'provider'
        && updated?.status === 'pending_supervisor_signature'
        && !updated?.[`${type}_signed_by`];
      if (softRouted) {
        toast.info('Routed to the assigned supervisor for signature', {
          description: "You are not this note's assigned supervisor, so it was sent to them instead. To sign anyway: Shift+click Sign, or right-click the signature area → \"Sign anyway\".",
          duration: 9000,
        });
      } else {
        toast.success(`Signed as ${type === 'supervisor_2' ? 'supervisor' : type}${forceSign ? ' (force signed)' : ''}`);
      }
      refreshNotes();
      refreshTodos();
      if (!softRouted) {
        // Let queue views (e.g. Supervision sign queue) clear the row immediately.
        window.dispatchEvent(new CustomEvent('v2-note-signed', { detail: { noteId, type } }));
      }
      try {
        const { data: fresh } = await axios.get(`${API}/notes/${noteId}`);
        if (fresh) {
          setSelectedNote(fresh);
          updateNoteStatusOptimistically(noteId, fresh.status, {
            [`${type}_signed_by`]: fresh[`${type}_signed_by`] ?? null,
            [`${type}_signed_at`]: fresh[`${type}_signed_at`] ?? null,
          });
        }
        if (softRouted) {
          // Nothing was signed — no post-sign dialogs.
          setNoteJustSigned(false);
        } else if (type === 'provider') {
          // Legacy parity: provider sign opens the rich "Note Signed
          // Successfully!" advancement dialog immediately (over the note);
          // suppress the close-time Next Steps dialog to avoid duplicates.
          setSignedNoteForAdvancement(fresh);
          setShowSignatureAdvanceDialog(true);
          setNoteJustSigned(false);
        } else {
          setNoteJustSigned(true);
        }
      } catch {
        // Refetch failed — fall back to the close-time Next Steps dialog.
        if (!softRouted) setNoteJustSigned(true);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sign note');
    }
  }, [noteId, user, updateNoteStatusOptimistically, refreshNotes, refreshTodos]);

  const handleSignNote = useCallback((type, withDate = true, customDate = null, forceSign = false) => {
    if (!user?.signature_image) {
      // Native V2 signature pad: draw + save to profile, then continue.
      setPendingSignType(type);
      pendingSignArgsRef.current = { withDate, customDate, forceSign };
      setShowSignaturePad(true);
      return;
    }
    signNoteDirect(type, withDate, customDate, forceSign);
  }, [user, signNoteDirect]);

  const handleSaveSignatureAndContinue = useCallback(async () => {
    if (!sigPadRef.current || sigPadRef.current.isEmpty()) {
      toast.error('Please provide a signature first');
      return;
    }
    setSavingSignature(true);
    try {
      const signatureImage = sigPadRef.current.toDataURL();
      await axios.put(`${API}/users/me/signature`, { signature_image: signatureImage });
      setUser(prev => (prev ? { ...prev, signature_image: signatureImage } : prev));
      toast.success('Signature saved to your profile');
      setShowSignaturePad(false);
      const args = pendingSignArgsRef.current || {};
      signNoteDirect(pendingSignType, args.withDate !== false, args.customDate || null, !!args.forceSign);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save signature to your profile');
    } finally {
      setSavingSignature(false);
    }
  }, [pendingSignType, signNoteDirect, setUser]);

  const handleUnsignNote = useCallback(async (type) => {
    // Legacy parity (App.js handleUnsignNote): permission check +
    // consequence-listing confirm before clearing the signature.
    const note = selectedNote;
    const signedBy = note?.[`${type}_signed_by`];
    if (signedBy !== user?.id && !isSuperAdmin(user)) {
      toast.error('You can only clear your own signature');
      return;
    }
    let confirmMessage = `Are you sure you want to remove your ${type} signature from this note?`;
    const consequences = [];
    const noteStatus = note?.status;
    if (type === 'provider') {
      if (noteStatus === 'pending_supervisor_signature' || noteStatus === 'sent_to_supervisor') {
        consequences.push('• The supervisor signature request will be cancelled');
      }
      if (noteStatus === 'sent_to_client_for_signature' || noteStatus === 'pending_client_signature') {
        consequences.push('• The client signature request will be pulled back');
        consequences.push('• The client will no longer see this note in their portal');
      }
      if (note?.supervisor_signed_at) {
        consequences.push("• The supervisor's signature will also be removed");
      }
      if (consequences.length > 0) {
        consequences.push('• The note will return to draft status');
      }
    } else if (type === 'supervisor') {
      if (noteStatus === 'sent_to_client_for_signature' || noteStatus === 'pending_client_signature') {
        consequences.push('• The client signature request will be pulled back');
        consequences.push('• The client will no longer see this note in their portal');
        consequences.push('• The note will return to provider-signed status');
      }
    }
    if (consequences.length > 0) {
      confirmMessage += '\n\nThis will also:\n' + consequences.join('\n');
    }
    const ok = await confirmAction(confirmMessage, {
      title: `Remove ${type} signature?`,
      confirmLabel: 'Remove Signature',
      destructive: true,
    });
    if (!ok) return;
    try {
      await axios.put(`${API}/notes/${noteId}`, {
        [`${type}_signed_at`]: null,
        [`${type}_signed_by`]: null,
      });
      toast.success('Signature removed from note');
      refreshNotes();
      refreshTodos();
      // Refresh the open note so the signature block disappears immediately
      try {
        const { data } = await axios.get(`${API}/notes/${noteId}`);
        if (data) setSelectedNote(data);
      } catch { /* list refresh already queued */ }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to remove signature');
    }
  }, [noteId, selectedNote, user, confirmAction, refreshNotes, refreshTodos]);

  const fetchTreatmentPlanGoals = useCallback(async (clientId) => {
    setLoadingTreatmentPlanGoals(true);
    try {
      const { data } = await axios.get(`${API}/clients/${clientId}/treatment-plan-goals`);
      setAvailableTreatmentPlanGoals(data?.plans || data || []);
    } catch {
      setAvailableTreatmentPlanGoals([]);
    } finally {
      setLoadingTreatmentPlanGoals(false);
    }
  }, []);

  const getSignatureUrl = useCallback(async (userId) => {
    try {
      const { data } = await axios.get(`${API}/users/${userId}/signature`);
      return data?.signature_image || null;
    } catch { return null; }
  }, []);

  const replaceClientVariables = useCallback((text, clientData) => {
    if (!text || !clientData) return text;
    return String(text)
      .replace(/\{client_name\}/gi, `${clientData.first_name || ''} ${clientData.last_name || ''}`.trim())
      .replace(/\{first_name\}/gi, clientData.first_name || '')
      .replace(/\{last_name\}/gi, clientData.last_name || '');
  }, []);

  const openNote = useCallback((note) => {
    if (!note?.id) return;
    // Flush pending edits before switching notes (dedupes by hash).
    noteEditingAutosaveRef.current?.flushSave?.();
    if (embedded) {
      setSearchParams((prev) => {
        const p = new URLSearchParams(prev);
        p.set('noteId', note.id);
        p.delete('mode');
        return p;
      }, { replace: true });
    } else {
      navigate(`/v2/note/${note.id}?return=${encodeURIComponent(returnTo)}`);
    }
  }, [embedded, setSearchParams, navigate, returnTo]);
  const handleNavigateToNote = openNote;

  const closeNoteWithCheck = useCallback(() => {
    const hasReal = noteEditingAutosave?.hasRealUnsavedChanges?.() ?? false;
    if (isEditingNote && hasReal) { setShowCloseEditingNoteWarning(true); return; }
    // Legacy parity (useNoteLifecycleController): closing a just-signed
    // note hands off to the Next Steps dialog instead of navigating away.
    // This path serves the note header X button; without it the dialog
    // only appeared on Escape/overlay close (user report Jun 2026).
    if (noteJustSigned) {
      setShowNextStepsDialog(true);
      setNoteJustSigned(false);
      setShowNoteDetail(false);
      return;
    }
    closeHost();
  }, [noteEditingAutosave, isEditingNote, noteJustSigned, closeHost]);

  // Write a field's value into the STRUCTURED note content (fields/sections)
  // so the renderer re-derives values and autosave serializes correctly.
  const applyFieldValues = useCallback((valueMap) => {
    setEditedNoteContent(prev => {
      if (!prev || typeof prev !== 'object') return prev;
      const setVal = (f) => (f.id && Object.prototype.hasOwnProperty.call(valueMap, f.id))
        ? { ...f, value: valueMap[f.id] } : f;
      if (Array.isArray(prev.fields)) {
        return { ...prev, fields: prev.fields.map(setVal) };
      }
      if (Array.isArray(prev.sections)) {
        return { ...prev, sections: prev.sections.map(s => ({ ...s, fields: (s.fields || []).map(setVal) })) };
      }
      return { ...prev, ...valueMap };
    });
  }, []);

  // Bridge for renderers that call setNewNoteFormData(prev => next) — basic
  // fields (text/checkbox/radio/select/file) route through this. Was a noop
  // (Jun 2026 fix): every basic-field edit in the V2 editor was silently
  // dropped (e.g. uncheckable checkboxes).
  const setNewNoteFormDataBridge = useCallback((updater) => {
    if (typeof updater !== 'function') return;
    setEditedNoteContent(prev => {
      if (!prev || typeof prev !== 'object') return prev;
      const current = {};
      if (Array.isArray(prev.fields)) {
        prev.fields.forEach(f => { if (f.id) current[f.id] = f.value; });
      } else if (Array.isArray(prev.sections)) {
        prev.sections.forEach(s => (s.fields || []).forEach(f => { if (f.id) current[f.id] = f.value; }));
      }
      const nextData = updater(current) || {};
      const changed = {};
      Object.keys(nextData).forEach(k => { if (nextData[k] !== current[k]) changed[k] = nextData[k]; });
      if (Object.keys(changed).length === 0) return prev;
      const setVal = (f) => (f.id && Object.prototype.hasOwnProperty.call(changed, f.id))
        ? { ...f, value: changed[f.id] } : f;
      if (Array.isArray(prev.fields)) {
        return { ...prev, fields: prev.fields.map(setVal) };
      }
      if (Array.isArray(prev.sections)) {
        return { ...prev, sections: prev.sections.map(s => ({ ...s, fields: (s.fields || []).map(setVal) })) };
      }
      return { ...prev, ...changed };
    });
  }, []);

  // Legacy-parity bridge (App.js handleFieldChangeLifted): specialty fields
  // like pan_select call handleFieldChange(prev => ({ ...prev, [id]: v })).
  // V2 previously treated the updater function as a fieldId and dropped the
  // write, so PANS values never reached editedNoteContent before save.
  const handleFieldChangeLifted = useCallback((updaterOrFieldId, maybeValue) => {
    if (typeof updaterOrFieldId === 'function') {
      setNewNoteFormDataBridge(updaterOrFieldId);
    } else {
      applyFieldValues({ [updaterOrFieldId]: maybeValue });
    }
  }, [setNewNoteFormDataBridge, applyFieldValues]);

  // Jun 2026 legacy-parity #4 — native linked-form handler (port of the
  // legacy App.js handleAddLinkedForm). Creates the triggered form as a
  // draft note on the same client, optionally creates a follow-up task.
  const handleAddLinkedForm = useCallback(async (linkedFormConfig, sourceNote) => {
    try {
      const templateResponse = await axios.get(`${API}/form-templates/${linkedFormConfig.linked_template_id}`);
      const template = templateResponse.data;
      const initialContent = {
        template_id: template.id,
        template_name: template.name,
        fields: template.form_fields?.map(f => ({
          ...f,
          value: f.type === 'checkbox_group' || f.type === 'multiselect' ? [] : ''
        })) || []
      };
      const noteData = {
        client_id: sourceNote.client_id,
        template_id: template.id,
        template_name: template.name,
        content: JSON.stringify(initialContent),
        status: 'draft',
        provider_id: user.id,
        billable: template.billable || false,
        requires_supervisor_signature: template.requires_supervisor_signature || false,
        source_note_id: sourceNote.id,
        source_note_name: sourceNote.template_name || 'Assessment'
      };
      const response = await axios.post(`${API}/notes`, noteData);
      const newNote = response.data;
      if (linkedFormConfig.create_task) {
        const clientName = sourceNote.client_name
          || `${selectedClient?.first_name || ''} ${selectedClient?.last_name || ''}`.trim();
        await axios.post(`${API}/tasks`, {
          title: `Complete ${linkedFormConfig.form_name} for ${clientName || 'Client'}`,
          description: `This form was triggered from ${sourceNote.template_name || 'an assessment'}. Please complete and sign.`,
          assigned_to: user.id,
          client_id: sourceNote.client_id,
          source_note_id: newNote.id,
          due_date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
          priority: 'medium',
          status: 'pending'
        });
      }
      setLinkedFormsAdded(prev => ({ ...prev, [linkedFormConfig.form_name]: newNote.id }));
      toast.success(`${linkedFormConfig.form_name} added to client's chart${linkedFormConfig.create_task ? ' and task created' : ''}`);
    } catch (error) {
      console.error('Error adding linked form:', error);
      toast.error(`Failed to add ${linkedFormConfig.form_name}: ${error.response?.data?.detail || error.message}`);
    }
  }, [user?.id, selectedClient?.first_name, selectedClient?.last_name]);

  const legacyFallback = useCallback((label) => () => {
    toast.info(`${label} isn't available in the V2 editor yet — opening the legacy editor.`);
    window.location.href = `/note/${noteId}?return=${encodeURIComponent(returnTo)}`;
  }, [noteId, returnTo]);
  const noop = useCallback(() => {}, []);

  // ── Native appointment dialog (Jun 2026 — "Open Appt" from the note
  // footer was a no-op in V2).  Wire the AppointmentsContext setters the
  // shared NoteDetailDialog calls, and mount AppointmentScheduleDialog
  // below (same pattern as ClientChartV2Dialog Phase 2H).
  const {
    setShowAppointmentForm: apptSetShowForm,
    setEditingAppointment: apptSetEditing,
    setAppointmentFormData: apptSetFormData,
  } = useAppointments();
  useV2BootstrapData();
  const refreshClientAppointments = useCallback(async () => {
    const cid = selectedNote?.client_id || selectedClient?.id;
    if (!cid) return;
    try {
      const r = await axios.get(`${API}/appointments?client_id=${cid}&summary=true`);
      const appts = Array.isArray(r.data) ? r.data : (r.data?.appointments || []);
      setClientAppointments(appts);
    } catch { /* non-fatal */ }
  }, [selectedNote?.client_id, selectedClient?.id]);
  const v2ApptLegacy = useV2AppointmentDialogLegacy({
    refreshAppointments: refreshClientAppointments,
  });
  // Legacy "Open Appointment" closes the note + switches to the calendar
  // tab; in V2 that would unmount this host mid-flight.  Instead, keep the
  // note open underneath and let the appointment dialog stack on top.
  const apptOpeningRef = useRef(false);
  const guardedSetEditingAppointment = useCallback((apt) => {
    if (apt) apptOpeningRef.current = true;
    apptSetEditing(apt);
  }, [apptSetEditing]);
  const guardedSetShowNoteDetail = useCallback((v) => {
    if (v === false && apptOpeningRef.current) {
      apptOpeningRef.current = false;
      return;
    }
    setShowNoteDetail(v);
  }, []);

  // close-warning handlers (shared save engine)
  const handleCloseEditingNoteSaveAndClose = useCallback(async () => {
    try {
      await noteEditingAutosave?.forceSave?.();
    } catch { /* surfaced by engine */ }
    setShowCloseEditingNoteWarning(false);
    closeHost();
  }, [noteEditingAutosave, closeHost]);
  const handleCloseEditingNoteDiscard = useCallback(() => {
    setShowCloseEditingNoteWarning(false);
    closeHost();
  }, [closeHost]);
  const handleCloseEditingNoteKeepEditing = useCallback(() => setShowCloseEditingNoteWarning(false), []);

  const getAccessibleCPTCodes = useCallback(() => cptCodes, [cptCodes]);
  // Jun 2026 V2 parity — was stubbed to [] which hid the post-sign
  // "Request Next Appointment Scheduling" option entirely (legacy App.js:7910).
  const getSchedulersForProvider = useCallback((providerId) => {
    const pool = effectiveUsers || [];
    if (!providerId) return [];
    return pool.filter(u => u.is_scheduler && u.scheduling_for && u.scheduling_for.includes(providerId));
  }, [effectiveUsers]);

  // Re-check CPT × insurance restriction when the Next Steps dialog opens
  // (same /api/validate-insurance-cpt call legacy makes, App.js ~L5476).
  useEffect(() => {
    if (!showNextStepsDialog || !selectedNote?.id) {
      setNextStepsCptBlocked(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const cpt = selectedNote.cpt_code
          || selectedNote._billing?.cpt_code
          || selectedNote.cpt_code_id;
        const clientId = selectedNote.client_id;
        if (!cpt || !clientId) {
          if (!cancelled) setNextStepsCptBlocked(false);
          return;
        }
        const resp = await axios.post(`${API}/validate-insurance-cpt`, {
          client_id: clientId,
          cpt_code: cpt,
        });
        if (!cancelled) setNextStepsCptBlocked(!!resp?.data?.primary_blocked);
      } catch {
        if (!cancelled) setNextStepsCptBlocked(false);
      }
    })();
    return () => { cancelled = true; };
  }, [showNextStepsDialog, selectedNote?.id, selectedNote?.cpt_code, selectedNote?.client_id]);

  // When the host dialog is dismissed, navigate back to V2 — unless the
  // post-sign Next Steps dialog is open (it renders after the note closes;
  // navigating immediately would unmount it before the user sees it).
  useEffect(() => {
    if (!showNoteDetail && !showNextStepsDialog && !showSignatureAdvanceDialog) closeHost();
  }, [showNoteDetail, showNextStepsDialog, showSignatureAdvanceDialog, closeHost]);

  // PANs for Comprehensive Assessment `pan_select` fields (Jun 2026 user
  // fix — V2 passed availablePANs: [] so the dropdown listed nothing).
  // Same endpoint + trigger condition as legacy openNote.
  const [availablePANs, setAvailablePANs] = useState([]);
  const noteHasPanField = useMemo(() => {
    let content = editedNoteContent || selectedNote?.content || {};
    if (typeof content === 'string') {
      try { content = JSON.parse(content); } catch { content = {}; }
    }
    const fieldList = Array.isArray(content.fields)
      ? content.fields
      : (Array.isArray(content.sections)
        ? content.sections.flatMap(s => s.fields || [])
        : []);
    return fieldList.some(f => f?.type === 'pan_select');
  }, [editedNoteContent, selectedNote?.content]);
  useEffect(() => {
    if (!selectedNote?.id) return;
    const tname = selectedNote.template_name || '';
    if (!/comprehensive assessment/i.test(tname) && !noteHasPanField) return;
    let cancelled = false;
    axios.get(`${API}/treatment-planning/pans`)
      .then(r => { if (!cancelled) setAvailablePANs(Array.isArray(r.data) ? r.data : []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [selectedNote?.id, selectedNote?.template_name, noteHasPanField]);

  const ctx = useMemo(() => ({
    API,
    NOTE_SIZE_PRESETS,
    allClientsForLookup: selectedClient ? [selectedClient] : [],
    appointments: clientAppointments,
    autosaveBlockedReason,
    availablePANs,
    availableTreatmentPlanGoals,
    calculateAge,
    canEditNote,
    canLockNote,
    clientAppointments,
    clientDiagnoses,
    clientGroups,
    clientMedications,
    clientMismatchInfo: null,
    clientNotes,
    clientVitals,
    clients: selectedClient ? [selectedClient] : [],
    closeNoteWithCheck,
    confirmAction,
    cptCodes,
    editContextMenuPos,
    editedNoteConsent,
    editedNoteContent,
    editingNote: isEditingNote ? selectedNote : null,
    fetchAppointments: refreshClientAppointments,
    fetchTreatmentPlanGoals,
    formTemplates: effectiveTemplates,
    formatDateMMDDYYYY,
    formatLocalDateTime,
    formatTimeTo12Hour,
    getAccessibleCPTCodes,
    getSignatureUrl,
    handleAddLinkedForm,
    handleCloseEditingNoteDiscard,
    handleCloseEditingNoteKeepEditing,
    handleCloseEditingNoteSaveAndClose,
    handleFieldChangeLifted,
    // v2-na: clientMismatchInfo is always null in the single-note route
    handleMismatchKeepEditing: noop,
    handleMismatchMoveNote: noop,
    handleMismatchSaveAndClose: noop,
    handleNavigateToNote,
    handleSignNote,
    openSignatureConfirm,
    handleUnsignNote,
    isEditingNote,
    isSavingNote,
    linkedFormsAdded,
    linkedGoals,
    loadedSignatures,
    locations,
    noteCreationError,
    noteDialogSize,
    noteEditingAutosave,
    noteHasClientVisibilityDivider: () => false,
    noteJustSigned,
    noteLinkedAppointment,
    notes: clientNotes,
    openNote,
    organizations,
    posCodes,
    providers,
    refreshClientNotes,
    refreshNotes,
    refreshTodos,
    replaceClientVariables,
    returnToCreateNote: null,
    selectedClient,
    selectedNote,
    serviceTypes,
    setActiveTab: noop, // v2-na: legacy dashboard tab nav — "Open Appt" uses the native dialog mounted below
    setAppointmentFormData: apptSetFormData,
    setAppointments: setClientAppointments,
    setAutosaveBlockedReason,
    setClientMedications,
    setClientNotes,
    setCorrectionsReason,
    setEditContextMenuPos,
    setEditedNoteConsent,
    setEditedNoteContent,
    setEditingAppointment: guardedSetEditingAppointment,
    setEditingMedication,
    setIsEditingNote,
    setIsSavingNote,
    setLinkedFormsAdded,
    setLinkedGoals,
    setLoadedSignatures,
    setNewNoteFormData: noop, // v2-na: only used by the returnToCreateNote flow (always null in V2); editor meta flows via setNewNoteFormDataBridge
    setNewNoteFormDataBridge,
    setNotBillableComment,
    setNotBillableReason,
    setNotBillableTargetNote,
    setNoteCreationError,
    setNoteJustSigned,
    setNoteLinkedAppointment,
    setNotes: setClientNotes,
    setReturnToCreateNote: noop, // v2-na: create-note flow state — returnToCreateNote is always null in V2
    setSelectedClient,
    setSelectedNote,
    setSelectedNoteClients: noop, // v2-na: create-note flow
    setSelectedNoteProvider: noop, // v2-na: create-note flow
    setSelectedNoteStatus: noop, // v2-na: create-note flow
    setSelectedTemplate: noop, // v2-na: create-note flow
    setSendToClientNote: noop, // v2-na: SendToClientDialog below receives noteId directly
    // v2-na: only reachable via the legacy returnToCreateNote back-button,
    // which V2 never sets (returnToCreateNote is always null here).
    setShowAddNote: noop,
    setShowAllDiagnoses: noop, // v2-na: dead prop — NoteDetailDialog destructures but never renders it
    setShowAppointmentForm: apptSetShowForm,
    setShowCloseEditingNoteWarning,
    setShowCorrectionsDialog,
    setShowDiagnosisForm,
    setShowEditContextMenu,
    setShowLinkGoalsDialog,
    setShowMedicationForm,
    setMedPlanCapture,
    setMedSnapshotCapture,
    setShowNextStepsDialog,
    setShowNotBillableDialog,
    setShowNoteDetail: guardedSetShowNoteDetail,
    setShowNoteHistory,
    setShowReferencePanel,
    setShowSendToClientDialog: setShowSendToClient,
    setShowSignatureContextMenu,
    setSignatureContextMenuPos,
    setSignatureContextMenuType,
    showAllDiagnoses: false,
    showCloseEditingNoteWarning,
    showEditContextMenu,
    showNoteDetail,
    showReferencePanel,
    showSignatureContextMenu,
    signatureContextMenuPos,
    signatureContextMenuType,
    updateNoteDialogSize: setNoteDialogSize,
    updateNoteStatusOptimistically,
    user,
    userRoleName,
    users: effectiveUsers,
  }), [
    selectedClient, clientAppointments, autosaveBlockedReason, availablePANs, availableTreatmentPlanGoals,
    canEditNote, canLockNote, clientDiagnoses, clientGroups, clientMedications, clientNotes,
    clientVitals, closeNoteWithCheck, confirmAction, cptCodes, editedNoteConsent,
    editedNoteContent, isEditingNote, selectedNote, fetchTreatmentPlanGoals, formTemplates, effectiveTemplates,
    getAccessibleCPTCodes, getSignatureUrl, legacyFallback, handleCloseEditingNoteDiscard,
    handleCloseEditingNoteKeepEditing, handleCloseEditingNoteSaveAndClose, handleFieldChangeLifted,
    handleNavigateToNote, handleSignNote, handleUnsignNote, isSavingNote, linkedFormsAdded,
    openSignatureConfirm,
    linkedGoals, loadedSignatures, locations, noteCreationError, noteDialogSize,
    noteEditingAutosave, noteJustSigned, noteLinkedAppointment, openNote, organizations,
    posCodes, providers, refreshClientNotes, refreshNotes, refreshTodos, replaceClientVariables,
    serviceTypes, showCloseEditingNoteWarning, showNoteDetail, showReferencePanel, updateNoteStatusOptimistically,
    user, userRoleName, users, effectiveUsers, noop,
    showSignatureContextMenu, signatureContextMenuPos, signatureContextMenuType,
    showEditContextMenu, editContextMenuPos,
    apptSetFormData, guardedSetEditingAppointment, apptSetShowForm, refreshClientAppointments, guardedSetShowNoteDetail,
  ]);

  // Jun 2026 — hold the loading spinner while a local draft is being
  // validated/restored so the user only ever sees the restored content
  // (no restore dialog, no flash of pre-restore content).
  if (loading || noteEditingAutosave.draftRestorePending) {
    return embedded ? (
      <div className="fixed inset-0 z-[999] bg-black/30 flex items-center justify-center" data-testid="v2-note-host-loading">
        <div className="h-9 w-9 animate-spin rounded-full border-[3px] border-white/40 border-t-white" aria-label="Loading" />
      </div>
    ) : (
      <div className="min-h-screen flex items-center justify-center bg-gray-50" data-testid="v2-note-host-loading">
        <div className="h-9 w-9 animate-spin rounded-full border-[3px] border-gray-300 border-t-teal-600" aria-label="Loading" />
      </div>
    );
  }
  if (loadError || !selectedNote) {
    return embedded ? (
      <div className="fixed inset-0 z-[999] bg-black/30 flex items-center justify-center" data-testid="v2-note-host-error">
        <div className="bg-white rounded-lg px-6 py-5 shadow-lg flex flex-col items-center gap-3">
          <p className="text-gray-700">{loadError || 'Note not found'}</p>
          <button className="text-teal-700 underline" onClick={closeHost} data-testid="v2-note-host-back-btn">Close</button>
        </div>
      </div>
    ) : (
      <div className="min-h-screen flex flex-col items-center justify-center gap-3 bg-gray-50" data-testid="v2-note-host-error">
        <p className="text-gray-700">{loadError || 'Note not found'}</p>
        <button className="text-teal-700 underline" onClick={closeHost} data-testid="v2-note-host-back-btn">Back</button>
      </div>
    );
  }

  return (
    <div data-testid="v2-note-host" className={embedded ? undefined : 'min-h-screen bg-gray-100'}>
      <NoteDetailDialog ctx={ctx} />
      {/* Native appointment dialog — serves the note footer "Open Appt" button (Jun 2026) */}
      <AppointmentScheduleDialog legacy={v2ApptLegacy} />
      <SignatureAdvanceDialog
        open={showSignatureAdvanceDialog}
        onOpenChange={setShowSignatureAdvanceDialog}
        note={signedNoteForAdvancement}
        clearNote={() => setSignedNoteForAdvancement(null)}
        user={user}
        clientsForLookup={selectedClient ? [selectedClient] : []}
        confirmAction={confirmAction}
        updateNoteStatusOptimistically={updateNoteStatusOptimistically}
        refreshNotes={refreshNotes}
        refreshTodos={refreshTodos}
        setSelectedNote={setSelectedNote}
        setShowNoteDetail={guardedSetShowNoteDetail}
        setShowSendToClientDialog={setShowSendToClient}
        setNotBillableTargetNote={setNotBillableTargetNote}
        setNotBillableReason={setNotBillableReason}
        setNotBillableComment={setNotBillableComment}
        setShowNotBillableDialog={setShowNotBillableDialog}
      />
      <SignatureConfirmDialog ctx={{
        API, refreshNotes, refreshTodos, selectedNote, setShowNoteDetail,
        setShowSignatureConfirm, setSignWithoutDate, setSignatureDate,
        showSignatureConfirm, signWithoutDate, signatureAction, signatureDate,
        updateNoteStatusOptimistically, user,
      }} />
      <CorrectionsDialog ctx={{
        API, correctionsReason, refreshNotes, refreshTodos, selectedNote,
        setCorrectionsReason, setShowCorrectionsDialog, setShowNoteDetail,
        showCorrectionsDialog, user,
      }} />
      <NotBillableDialog ctx={{
        API, notBillableComment, notBillableReason, notBillableTargetNote,
        noteReviewData: null, refreshNotes, refreshTodos, setNotBillableComment,
        setNotBillableReason, setNotBillableTargetNote, setShowNotBillableDialog,
        setShowNoteReviewDialog: noop, // v2-na: no review dialog mounted in the note host
        showNotBillableDialog, updateNoteStatusOptimistically,
      }} />
      <LinkGoalsDialog ctx={{
        availableTreatmentPlanGoals, linkedGoals, loadingTreatmentPlanGoals,
        setLinkedGoals, setSelectedNote, setShowLinkGoalsDialog, showLinkGoalsDialog,
      }} />
      <NextStepsDialog ctx={{
        API, formTemplates: effectiveTemplates, getSchedulersForProvider, nextStepsCptBlocked,
        refreshNotes, refreshTodos, selectedNote, setNotBillableComment,
        setNotBillableReason, setNotBillableTargetNote, setScheduleNextData,
        setSelectedNote, setShowNextStepsDialog, setShowNotBillableDialog,
        setShowNoteDetail, setShowScheduleNextDialog, showNextStepsDialog,
        updateNoteStatusOptimistically, user,
      }} />
      {/* Jun 2026 V2 parity — "Request Next Appointment Scheduling" in
          NextStepsDialog was noop-stubbed (the dialog only existed inline
          in legacy App.js). */}
      <ScheduleNextDialog ctx={{
        API, getSchedulersForProvider, scheduleNextData, selectedClient,
        selectedNote, setScheduleNextData, setShowScheduleNextDialog,
        showScheduleNextDialog, user,
        onSent: () => { refreshNotes(); refreshTodos(); },
      }} />
      {/* Jun 2026 — Send to Client natively in V2 (was a legacy-editor
          bounce). Same self-contained dialog the legacy dashboard mounts;
          tenant scoping via the shared axios instance/JWT. On success the
          dialog stays open to surface the portal "Copy link" UI; closing
          it afterwards returns to the V2 origin (documents tab etc.). */}
      <SendToClientDialog
        open={showSendToClient}
        onOpenChange={(o) => {
          setShowSendToClient(o);
          if (!o && sentToClientRef.current) {
            sentToClientRef.current = false;
            closeHost();
          }
        }}
        noteId={selectedNote?.id}
        alreadySigned={!!(selectedNote?.client_signature || selectedNote?.client_signed_at)}
        onSuccess={() => {
          sentToClientRef.current = true;
          refreshNotes();
          refreshTodos();
          refreshClientNotes();
        }}
      />
      <NoteHistoryPanel
        noteId={selectedNote?.id}
        isOpen={showNoteHistory}
        onClose={() => setShowNoteHistory(false)}
        onRestoreComplete={async () => {
          refreshNotes();
          refreshClientNotes();
          // Jun 2026 fix (user report: restore "only works in legacy"):
          // refetch the open note so the restored content is visible
          // immediately — legacy did this, V2 didn't.
          try {
            const { data: fresh } = await axios.get(`${API}/notes/${selectedNote.id}`);
            setSelectedNote(fresh);
            let parsed = fresh.content;
            if (typeof parsed === 'string') { try { parsed = JSON.parse(parsed); } catch { parsed = {}; } }
            parsed = (parsed && typeof parsed === 'object') ? parsed : {};
            if (parsed.fields?.length > 0 || parsed.sections?.length > 0) {
              const template = await resolveNoteTemplate({
                noteData: parsed, fullNote: fresh, templates: effectiveTemplates, api: API,
              });
              parsed = mergeNoteContentWithTemplate(parsed, fresh, template);
            }
            setEditedNoteContent(parsed);
            setLinkedGoals(fresh.linked_goals || []);
          } catch { /* keep stale view if refetch fails */ }
        }}
        isNoteLocked={selectedNote?.status === 'locked' || selectedNote?.status === 'signed' || selectedNote?.is_locked}
      />
      {/* iframe-safe confirm dialog (legacy App.js parity) — driven by
          confirmAction(...), resolves true/false. */}
      <AlertDialog
        open={confirmState.open}
        onOpenChange={(open) => {
          if (!open && confirmState.resolver) closeConfirm(false);
        }}
      >
        <AlertDialogContent data-testid="v2-iframe-safe-confirm-dialog" className="z-[1000001]">
          <AlertDialogHeader>
            <AlertDialogTitle>{confirmState.title}</AlertDialogTitle>
            <AlertDialogDescription style={{ whiteSpace: 'pre-line' }}>
              {confirmState.message}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="v2-iframe-safe-confirm-cancel"
              onClick={() => closeConfirm(false)}
            >
              {confirmState.cancelLabel}
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="v2-iframe-safe-confirm-ok"
              className={confirmState.destructive ? 'bg-red-600 hover:bg-red-700 text-white' : ''}
              onClick={() => closeConfirm(true)}
            >
              {confirmState.confirmLabel}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <Dialog open={showSignaturePad} onOpenChange={setShowSignaturePad}>
        <DialogContent className="max-w-lg" data-testid="v2-signature-pad-dialog">
          <DialogHeader>
            <DialogTitle>Create Your Signature</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-600">
            Draw your signature below. It will be saved to your profile and applied when you sign this note.
          </p>
          <div className="border-2 border-dashed border-gray-300 rounded-lg bg-white">
            <LazySignaturePad
              ref={sigPadRef}
              penColor="black"
              canvasProps={{ width: 460, height: 160, className: 'w-full rounded-lg', 'data-testid': 'v2-signature-pad-canvas' }}
            />
          </div>
          <div className="flex justify-between gap-2">
            <Button variant="ghost" onClick={() => sigPadRef.current?.clear()} data-testid="v2-signature-pad-clear-btn">
              Clear
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowSignaturePad(false)} data-testid="v2-signature-pad-cancel-btn">
                Cancel
              </Button>
              <Button
                className="bg-teal-600 hover:bg-teal-700 text-white"
                disabled={savingSignature}
                onClick={handleSaveSignatureAndContinue}
                data-testid="v2-signature-pad-save-btn"
              >
                {savingSignature ? 'Saving…' : 'Save & Sign Note'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      {/* Jun 2026 — native Add/Edit Medication + Add Diagnosis dialogs
          serving the note-field "+ Add" buttons (legacy parity). */}
      <AddEditMedicationDialog
        open={showMedicationForm}
        onOpenChange={(open) => {
          setShowMedicationForm(open);
          if (!open) {
            setEditingMedication(null);
            setMedPlanCapture(null);
            setMedSnapshotCapture(null);
          }
        }}
        client={selectedClient}
        editingMedication={editingMedication}
        onCapture={medPlanCapture}
        onSuccess={async (cid2, extra) => {
          setShowMedicationForm(false);
          setEditingMedication(null);
          if (extra?.createdMedication && medSnapshotCapture) {
            medSnapshotCapture(extra.createdMedication);
            setMedSnapshotCapture(null);
          }
          const cid = selectedNote?.client_id || selectedClient?.id;
          if (cid) {
            try {
              const r = await axios.get(`${API}/clients/${cid}/medications`);
              setClientMedications(r.data || []);
            } catch { /* keep stale list */ }
          }
        }}
      />
      <AddDiagnosisDialog
        open={showDiagnosisForm}
        onOpenChange={setShowDiagnosisForm}
        client={selectedClient}
        users={effectiveUsers || []}
        currentUser={user}
        onSuccess={async () => {
          setShowDiagnosisForm(false);
          toast.success('Diagnosis added');
          const cid = selectedNote?.client_id || selectedClient?.id;
          if (cid) {
            try {
              const r = await axios.get(`${API}/clients/${cid}/diagnoses`);
              setClientDiagnoses(r.data || []);
            } catch { /* keep stale list */ }
          }
        }}
      />
    </div>
  );
}
